import {
  AssessmentData,
  checkVisibility,
  Element,
  ElementKind,
  matchElementRange,
  QuestionData,
  RecommendationFragmentType,
  SectionData,
  WidgetData,
} from '@playbooks/action-cards-utils/src'
import Recommendation from '../features/recommendation/Recommendation'
import Question from '../features/question/Question'
import Assessment from '../features/assessment/Assessment'
import { useMemo } from 'react'
import { SectionContextProvider } from '../contexts/SectionContext'
import ElementWrapper from './ElementWrapper'
import { useAppContext } from '../contexts/AppContext'
import useOnSectionReady from '../hooks/useOnSectionReady'
import { WidgetContext } from '../features/widgets/widgetTypes'
import useScopeSections from '../hooks/useScopeSections'
import Widget from '../features/widgets/widget'

interface SectionProps {
  data: SectionData
}
export default function Section({ data }: SectionProps) {
  const {
    data: actionCardsData,
    actionCardsModel,
    settings: { isWebplay },
  } = useAppContext()
  const sectionRef = useOnSectionReady(data)

  const elements = useMemo(() => {
    return data.elements?.filter(element => {
      if (element.kind === ElementKind.RECOMMENDATION) {
        const recommendationFragment = element as RecommendationFragmentType

        return recommendationFragment.hideFromWebPlay && isWebplay
          ? false
          : checkVisibility(element, actionCardsData) &&
              matchElementRange(
                {
                  idOfSep: data.idOfSep,
                  scoreBased: recommendationFragment.scoreBased!,
                  minScore: recommendationFragment.minScore!,
                  maxScore: recommendationFragment.maxScore!,
                  scoringDimensionId: recommendationFragment.scoringDimensionId!,
                },
                actionCardsData,
              )
      }

      return checkVisibility(element, actionCardsData)
    })
  }, [data, actionCardsData])

  const isHeaderLocked = !!actionCardsData.sections.find(sec => sec.id === data.idOfSep)?.isLocked
  const isSectionLocked = actionCardsData.isLocked || isHeaderLocked
  const headerScopeSections = useScopeSections(actionCardsModel, data)

  const widgetsContext = useMemo(() => {
    const widgetElements =
      data.elements?.filter(element => {
        return element.kind === ElementKind.WIDGET
      }) || []

    return {
      allWidgets: [...widgetElements],
      // We set empty texts for widget, as we create
      // this contexts out of a recommendation
      visibleText: '',
      originalText: '',
      section: data,
      headerScopeSections,
    } as WidgetContext
  }, [actionCardsData])

  return (
    <SectionContextProvider isLocked={isSectionLocked}>
      <div ref={sectionRef} data-section id={`${data.id}|${data.idOfSep}`} className="flex flex-col gap-5">
        {elements?.map(element => {
          switch (element.kind) {
            case ElementKind.RECOMMENDATION:
              const recommendationFragment = element as RecommendationFragmentType

              return (
                <ElementWrapper
                  key={element.id}
                  locked={isSectionLocked}
                  component={
                    <Recommendation
                      key={recommendationFragment.text}
                      data={recommendationFragment}
                      sectionData={data}
                    />
                  }
                />
              )
            case ElementKind.QUESTION:
              return (
                <ElementWrapper
                  key={element.id}
                  locked={isSectionLocked}
                  component={<Question data={element as QuestionData} />}
                />
              )
            case ElementKind.ASSESSMENT:
              return (
                <ElementWrapper
                  key={element.id}
                  locked={isSectionLocked}
                  component={<Assessment data={element as AssessmentData} />}
                />
              )
            case ElementKind.WIDGET:
              const widgetElement = element as WidgetData

              return (
                <ElementWrapper
                  key={element.id}
                  locked={isSectionLocked}
                  component={<Widget key={widgetElement.id} widget={widgetElement} context={widgetsContext} />}
                />
              )
            default:
              return <div>Unsupported element kind: {(element as Element).kind}</div>
          }
        })}
      </div>
    </SectionContextProvider>
  )
}
